/**
 * borrowed from freshaid
 * https://github.com/datamole-ai/freshaid-application/blob/main/apps/frontend/src/components/Inputs/ComboboxMultiple.tsx
 */

import {
    Combobox as ComboboxWDS,
    ComboboxTrigger as ComboboxTriggerWDS, // HOTFIX: `Combobox.Trigger` should be instead, but it doesn't work
    useComboboxMultiple,
    type UseComboboxMultipleArgs,
    // @ts-expect-error FIXME missing types in WDS component
} from "@datamole/wds-component-combobox";
import clsx from "clsx";
import { type MouseEventHandler, type ReactNode } from "react";

import styles from "@/components/ui/ComboboxMultiple/ComboboxMultiple.module.css";
import { ComboboxMultipleFooter } from "@/components/ui/ComboboxMultiple/ComboboxMultipleFooter";
import { CheckIcon, XMarkIcon } from "@/components/ui/Icons";
import { Loader } from "@/components/ui/Loader";
import { Button } from "@/components/ui/buttons/Button";

type Props<TOption, TValue> = UseComboboxMultipleArgs<TOption, TValue> & {
    isLoading?: boolean;
    triggerClassName?: string;
    popoverClassName?: string;
    searchBoxPlaceholder?: string;
    showClearButton?: boolean;
    emptyOptionsMessage?: ReactNode;
    // to limit the number of value options shown in the trigger and disabling wrapping of value options
    limitMaxValueOptions?: number;
    getOptionKey: (option: TOption) => string;
    renderValueOption?: (
        valueOption: TOption,
        // HOTFIX: Combobox should export type returned from 'getValueMultipleItemProps'
        componentProps: { disabled?: boolean; onRemove: () => void; key: string | number; removeIcon?: ReactNode },
        index: number,
    ) => ReactNode;
    renderOption?: (
        option: TOption,
        // HOTFIX: Combobox should export type returned from 'getOptionProps'
        componentProps: {
            key: string | number;
            iconSelected: ReactNode;
            isSelected: boolean;
            onClick: () => void;
            id: string;
            role: string;
            tabIndex: number;
            isHighlighted: boolean | undefined;
            onPointerMove: MouseEventHandler<HTMLElement>;
        },
        index: number,
    ) => ReactNode;
};

const ComboboxMultiple = <TOption, TValue>(props: Props<TOption, TValue>) => {
    const {
        isLoading,
        triggerClassName,
        popoverClassName,
        searchBoxPlaceholder = "Type to search",
        emptyOptionsMessage = "No results found.",
        showClearButton,
        limitMaxValueOptions,
        getOptionKey,
        getOptionLabel,
        getOptionValue,
        renderValueOption,
        renderOption,
        value,
        onChange,
        ...otherProps
    } = props;

    const combobox = useComboboxMultiple<TOption, TValue>({
        ...otherProps,
        value,
        onChange,
        getOptionLabel,
        getOptionValue,
    });

    const handleClear = () => {
        onChange([]);
    };

    const valueOptionsToShow = limitMaxValueOptions
        ? combobox.valueOptions.slice(0, limitMaxValueOptions)
        : combobox.valueOptions;

    const numValueOptionsOverflow = limitMaxValueOptions ? combobox.valueOptions.length - limitMaxValueOptions : 0;

    return (
        <ComboboxWDS {...combobox.getRootProps()}>
            <ComboboxTriggerWDS className={triggerClassName}>
                <ComboboxTriggerWDS.ValueMultiple
                    className={clsx(numValueOptionsOverflow && styles.value_multiple_no_wrap)}
                >
                    {/* @ts-expect-error FIXME missing typs in WDS component */}
                    {valueOptionsToShow.map((valueOption, index) =>
                        renderValueOption ? (
                            renderValueOption(
                                valueOption,
                                {
                                    key: getOptionKey(valueOption),
                                    ...combobox.getValueMultipleItemProps(getOptionValue(valueOption)),
                                },
                                index,
                            )
                        ) : (
                            <ComboboxTriggerWDS.ValueMultiple.Item
                                key={getOptionKey(valueOption)}
                                {...combobox.getValueMultipleItemProps(getOptionValue(valueOption))}
                            >
                                {getOptionLabel(valueOption)}
                            </ComboboxTriggerWDS.ValueMultiple.Item>
                        ),
                    )}
                    {numValueOptionsOverflow > 0 && <span>{`+${numValueOptionsOverflow}`}</span>}
                </ComboboxTriggerWDS.ValueMultiple>
                <ComboboxTriggerWDS.Actions>
                    <ComboboxTriggerWDS.Remove />
                    <ComboboxTriggerWDS.Toggle />
                </ComboboxTriggerWDS.Actions>
            </ComboboxTriggerWDS>
            <ComboboxWDS.Content {...combobox.getContentProps()} className={popoverClassName}>
                <ComboboxWDS.Content.SearchBox
                    className={styles.search_box}
                    placeholder={searchBoxPlaceholder}
                    {...combobox.getSearchBoxProps()}
                />

                <ComboboxWDS.Content.List {...combobox.getListProps()}>
                    {isLoading ? (
                        <ComboboxWDS.Content.Info>
                            <Loader size={"16px"} />
                        </ComboboxWDS.Content.Info>
                    ) : combobox.options.length < 1 ? (
                        <ComboboxWDS.Content.Info>{emptyOptionsMessage}</ComboboxWDS.Content.Info>
                    ) : (
                        // @ts-expect-error FIXME missing types in WDS component
                        combobox.options.map((option, index) =>
                            renderOption ? (
                                renderOption(
                                    option,
                                    {
                                        key: getOptionKey(option),
                                        ...combobox.getOptionProps(index),
                                        iconSelected: <CheckIcon />,
                                    },
                                    index,
                                )
                            ) : (
                                <ComboboxWDS.Content.List.Option
                                    key={getOptionKey(option)}
                                    {...combobox.getOptionProps(index)}
                                    iconSelected={<CheckIcon />}
                                >
                                    {getOptionLabel(option)}
                                </ComboboxWDS.Content.List.Option>
                            ),
                        )
                    )}
                </ComboboxWDS.Content.List>

                {showClearButton && (
                    <ComboboxMultipleFooter>
                        <Button
                            onClick={handleClear}
                            disabled={value.length === 0}
                            variant="outline"
                            size="sm"
                            type="button"
                            startIcon={<XMarkIcon />}
                        >
                            Clear Filter
                        </Button>
                    </ComboboxMultipleFooter>
                )}
            </ComboboxWDS.Content>
        </ComboboxWDS>
    );
};

const ComboboxMultipleValueOption = ComboboxTriggerWDS.ValueMultiple.Item;
const ComboboxMultipleOption = ComboboxWDS.Content.List.Option;

export { ComboboxMultiple, ComboboxMultipleValueOption, ComboboxMultipleOption };
export type { Props as ComboboxMultipleProps };
